import type { IncidentReportItem } from '@lmc/ts-common'
import { getJSON } from 'common/services/api'
import { useQuery } from 'react-query'

type UseIncidentReportByLogProps = {
  incidentLogId: string | undefined
  onCreateSuccess?: () => void
  onUpdateSuccess?: () => void
  onDeleteSuccess?: () => void
  /**
   * @default true
   */
  enabled?: boolean
  /**
   * Fetch from the server on mount even when the cached copy is still fresh.
   * @default false
   */
  alwaysRefetchOnMount?: boolean
}

export const GET_INCIDENT_REPORT_BY_LOG_URL = `/api/list/incident-report/by-log`

const useIncidentReportByLog = <
  T extends IncidentReportItem = IncidentReportItem
>({
  incidentLogId,
  enabled = true,
  alwaysRefetchOnMount = false
}: UseIncidentReportByLogProps) => {
  const incidentLogIdExists = incidentLogId !== undefined
  const isEnabled = incidentLogIdExists && enabled === true

  const {
    data,
    isLoading: isIncidentReportLoading,
    isFetchedAfterMount,
    isError,
    refetch
  } = useQuery<{
    data: T
  }>(
    ['incident-report', incidentLogId],
    () =>
      getJSON({ url: `${GET_INCIDENT_REPORT_BY_LOG_URL}/${incidentLogId}` }),
    {
      enabled: isEnabled,
      ...(alwaysRefetchOnMount ? { refetchOnMount: 'always' as const } : {})
    }
  )

  return {
    incidentReport: data?.data,
    isIncidentReportLoading,
    isFetchedAfterMount,
    isError,
    refetch
  }
}

export default useIncidentReportByLog
